// src/App.js - Versión simplificada con sistema de URLs funcionando
import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import TopChollosPage from './components/pages/TopChollosPage';
import Seo from './components/common/Seo';
import { site } from './config/site';
import Navigation from './components/layout/Navigation';
import SelectorPaisInicial from './components/common/SelectorPaisInicial';
// Pie de autoría: listo pero sin mostrar. Ver el comentario junto a <Footer />.
// import Footer from './components/layout/Footer';
import { Header } from './components/layout/Header';
import { LoginModal } from './components/auth/LoginModal';
import { BackToTop } from './components/ui/BackToTop';
import { BannerBienvenida } from './components/common/BannerBienvenida';
import { ActualizacionApp } from './components/common/ActualizacionApp';
import { InstalarApp } from './components/common/InstalarApp';
import { BotonInstalar } from './components/common/BotonInstalar';

// Hooks
import { seccionDisponible } from './utils/countryUtils';
import { useAuth } from './hooks/useAuth';
import { useUserProfile } from './hooks/useUserProfile';
import { useCountry } from './hooks/useCountry';
import { useActualizacion } from './hooks/useActualizacion';

// Top Ofertas es la vista por defecto y va en el bundle inicial; las demás se
// descargan al visitarlas.
const TodosChollosPage = lazy(() => import('./components/pages/TodosChollosPage'));
const TrackingPage = lazy(() => import('./components/pages/TrackingPage'));
const ViajesPage = lazy(() => import('./components/pages/ViajesPage'));
const ExclusivosPage = lazy(() => import('./components/pages/ExclusivosPage'));

// Utilidades para URL (inline para evitar problemas de importación)

const VALID_PAGES = {
  'TodosChollosPage':  'todos',
  'TopChollosPage':    'top',
  'TrackingPage':      'tracking',
  'ViajesPage':        'viajes',
  'ExclusivosPage':    'exclusivos',
};

const DEFAULT_PAGE = 'top';

// Título y descripción de cada vista (pestaña del navegador y metadatos). Top
// Ofertas no lleva título propio: usa el del sitio, igual que index.html.
const SEO_POR_PAGINA = {
  top: {},
  todos: {
    title: 'Explorar todas las ofertas',
    description: 'Todas las ofertas y chollos reunidos en un solo lugar: filtrá por tienda, categoría y descuento y compará precios.',
  },
  viajes: {
    title: 'Ofertas de vuelos y hoteles',
    description: 'Promociones de vuelos y hoteles en un solo lugar, para encontrar el mejor momento de viajar.',
  },
  exclusivos: {
    title: 'Ofertas exclusivas',
    description: 'Ofertas de tiendas seleccionadas que sólo se ven con una cuenta gratuita de CholloMan.',
  },
  tracking: {
    title: 'Alertas de precio',
    description: 'Creá alertas y CholloMan te avisa por Telegram cuando el producto que querés baja de precio.',
  },
};

/**
 * Qué vista pide la URL.
 *
 * El hash llevaba una segunda parte con la tienda (`#TodosChollosPage#Zara`),
 * de cuando la app abría filtrada por una tienda fija. Eso se retiró: hoy la
 * tienda se elige dentro de Explorar y en Top Ofertas hay un carrusel por cada
 * una. El parámetro quedó dando vueltas —forzando 'Zara' en cada navegación—
 * sin que ninguna página lo leyera.
 *
 * Los hashes viejos siguen funcionando: lo que venga después de la página se
 * ignora en vez de romper.
 */
const parseUrlHash = (hash) => {
  if (!hash) return { page: DEFAULT_PAGE };

  const [pageParam] = hash.split('#').filter(Boolean);
  return { page: VALID_PAGES[pageParam] || DEFAULT_PAGE };
};

const PAGINA_A_HASH = {
  todos:      'TodosChollosPage',
  top:        'TopChollosPage',
  tracking:   'TrackingPage',
  viajes:     'ViajesPage',
  exclusivos: 'ExclusivosPage',
};

const updateBrowserUrl = (page) => {
  const hash = `#${PAGINA_A_HASH[page] || 'TopChollosPage'}`;
  if (window.location.hash !== hash) {
    window.history.pushState(null, null, hash);
  }
};

// Datos estructurados de la organización (la home). Sin teléfono ni correo a
// propósito: el sitio no publica un contacto directo (ver el Footer desactivado).
// El contacto es Telegram.
const JSON_LD_ORGANIZACION = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: site.name,
  url: `${site.url}/`,
  logo: site.logo,
  description: site.description,
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    url: site.telegram[1],
    availableLanguage: 'Spanish',
  },
  sameAs: site.telegram,
};

const App = () => {
  // Estados básicos
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Hooks
  const { user, setUser, authListo, checkAuthStatus, logout } = useAuth();
  const { updateUserProfile } = useUserProfile();
  // `useCountry` ya garantiza un país en servicio: si la URL o lo guardado
  // apuntan a uno retirado, resuelve al que sí tiene catálogo.
  const {
    country, setCountry,
    faltaElegir, deteccion, elegirPaisInicial, adoptarPreferencia,
  } = useCountry();

  // Versión nueva publicada: el aviso aparece solo. `abrirActualizacion` es
  // para cuando el usuario sospecha de la caché y quiere forzarla a mano.
  const { disponible: versionNueva, descartar: descartarVersion } = useActualizacion();
  const [mostrarActualizacion, setMostrarActualizacion] = useState(false);
  const [mostrarInstalar, setMostrarInstalar] = useState(false);

  // Lee la vista de la URL y la aplica al estado.
  const parseCurrentUrl = useCallback(() => {
    const { page } = parseUrlHash(window.location.hash);
    setCurrentPage(page);
    return { page };
  }, []);

  // Botón atrás/adelante del navegador.
  const handleHashChange = useCallback(() => {
    parseCurrentUrl();
  }, [parseCurrentUrl]);

  const navigateToPage = useCallback((page) => {
    setCurrentPage(page);
    updateBrowserUrl(page);
  }, []);

  // Una sección que no existe en el país elegido no puede quedar montada: quien
  // está en Viajes y cambia a España se quedaría mirando promos aéreas argentinas
  // en una vitrina española, y la pestaña ya ni siquiera figura en el menú.
  // Se lo devuelve a Top Ofertas, que existe en los dos.
  useEffect(() => {
    if (!seccionDisponible(currentPage, country)) {
      navigateToPage('top');
    }
  }, [country, currentPage, navigateToPage]);

  const handlePageChange = useCallback((page) => {
    navigateToPage(page);
    // Cambiar de sección no arrastra la búsqueda anterior.
    setSearchQuery('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigateToPage]);

  // `searchQuery` sigue existiendo porque «Explorar» lo usa como estado
  // controlado —lo lee al montarse y lo limpia al cambiar de sección—, pero ya
  // no lo escribe nadie más: quien busca lo hace desde el buscador de la propia
  // página.

  // Funciones básicas
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Modo con el que se abre el modal de acceso. Quien llega desde "Avísame
  // cuando esté lista" quiere registrarse, no iniciar sesión: mandarlo al
  // formulario de acceso sería un paso de más.
  const [loginModalMode, setLoginModalMode] = useState('login');

  const handleLoginClick = (modo = 'login') => {
    setLoginModalMode(typeof modo === 'string' ? modo : 'login');
    setIsLoginModalOpen(true);
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsLoginModalOpen(false);
  };

  // 🔥 FUNCIÓN: Renderizar página actual
  const renderCurrentPage = () => {
    if (!isInitialized) {
      return (
        <div id="app-cargando" className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-xl text-gray-700">Cargando...</p>
          </div>
        </div>
      );
    }

    switch (currentPage) {
      case 'top':
        return (
          <TopChollosPage country={country} />
        );
      case 'viajes':
        return <ViajesPage />;
      case 'exclusivos':
        return (
          <ExclusivosPage
            user={user}
            onLoginClick={handleLoginClick}
            country={country}
          />
        );
      case 'tracking':
        return (
          <TrackingPage
            user={user}
            onLoginClick={handleLoginClick}
            country={country}
          />
        );
      case 'todos':
      default:
        return (
          <TodosChollosPage
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            country={country}
          />
        );
    }
  };

  // Estado inicial a partir de la URL.
  useEffect(() => {
    parseCurrentUrl();
    setIsInitialized(true);
  }, [parseCurrentUrl]);

  // 🔥 EFECTO: Escuchar cambios en hash
  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [handleHashChange]);

  // Effects básicos
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Quien ya dijo en su cuenta dónde quiere estar no tiene que decirlo otra vez.
  //
  // Se espera a `authListo` en vez de mirar `user` a secas: la comprobación de
  // sesión es asíncrona, y sin esperarla el diálogo aparecería un instante para
  // alguien que entra desde un dispositivo nuevo pero ya tiene su país
  // guardado en el perfil. Un parpadeo así se lee como un error.
  // Y se adopta una sola vez por cada preferencia: la clave es cuenta + país
  // guardado. Antes se adoptaba en cada pasada del efecto, y como el efecto se
  // volvía a disparar al cambiar de país, elegir España desde el encabezado
  // rebotaba a Argentina en el render siguiente.
  //
  // La clave lleva el país y no sólo la cuenta para que guardar otra preferencia
  // en «Mi cuenta» sí mueva el catálogo: eso es una decisión nueva, no la misma
  // repetida.
  const preferenciaAdoptada = useRef(null);
  useEffect(() => {
    if (!authListo) return;
    const pref = user?.pais_preferido || null;
    if (!pref) { preferenciaAdoptada.current = null; return; }   // sin sesión o sin preferencia
    const clave = `${user?.id ?? user?.email ?? ''}:${pref}`;
    if (preferenciaAdoptada.current === clave) return;
    preferenciaAdoptada.current = clave;
    adoptarPreferencia(pref);
  }, [authListo, user, adoptarPreferencia]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowBackToTop(scrollTop > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div id="app-root" className="App" style={{ background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header global */}
      <Header
        user={user}
        onLoginClick={handleLoginClick}
        onLogout={logout}
        updateUserProfile={updateUserProfile}
        country={country}
        onCountryChange={setCountry}
        onActualizarApp={() => setMostrarActualizacion(true)}
        onInstalarApp={() => setMostrarInstalar(true)}
      />

      {/* Presentación para quien entra por primera vez. Lo que distingue a la
          aplicación —que las alertas buscan el producto por vos— no se deduce
          mirando la portada: hay que contarlo. Ver docs/PROMOCION.md. */}
      <BannerBienvenida
        country={country}
        onCrearAlerta={() => {
          // Sin cuenta no hay alertas: primero registrarse, y ya con sesión
          // la pestaña de Alertas queda a un clic.
          if (user) handlePageChange('tracking');
          else handleLoginClick('registro');
        }}
        onVerOfertas={() => handlePageChange('top')}
      />

      {/* Navegación entre páginas */}
      <Navigation
        currentPage={currentPage}
        onPageChange={handlePageChange}
        country={country}
      />
      
      {/* Metadatos de la vista actual. El canonical es siempre `/`: Google
          ignora el fragmento (#), así que todas las vistas son una sola URL. */}
      <Seo
        {...(SEO_POR_PAGINA[currentPage] || SEO_POR_PAGINA.top)}
        locale={country === 'AR' ? 'es_AR' : 'es_ES'}
        jsonLd={currentPage === 'top' ? JSON_LD_ORGANIZACION : null}
      />

      {/* Contenido de la página actual. El id lleva el nombre de la página
          activa, así que en el inspector se ve de una qué vista está montada. */}
      <main id={`app-main-${currentPage}`} data-page={currentPage}>
        <Suspense fallback={null}>
          {renderCurrentPage()}
        </Suspense>
      </main>

      {/* Pie con el crédito de autoría. DESACTIVADO a propósito, no a medio
          hacer: está terminado y probado, pero exponer un contacto directo en
          un sitio público invita a mensajes que hoy no hay quién atienda.
          Para activarlo, descomentar esta línea y su `import` de más arriba.

          Va después de <main> y no dentro, para quedar al final del documento
          sea cual sea la vista sin tener que ponerlo en cada página. */}
      {/* <Footer /> */}

      {/* Primera visita: qué catálogo se va a mostrar y por qué.
          Se espera a que la sesión esté resuelta para no preguntarle el país a
          quien ya lo tiene guardado en su cuenta. */}
      {faltaElegir && authListo && (
        <SelectorPaisInicial deteccion={deteccion} onElegir={elegirPaisInicial} />
      )}

      {/* Modal de login – abierto desde el header */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLoginSuccess}
        modoInicial={loginModalMode}
      />

      {/* Instalación: el acceso flotante se esconde solo si ya está instalada
          o si el usuario lo descartó; desde el perfil sigue siempre disponible. */}
      <BotonInstalar onAbrir={() => setMostrarInstalar(true)} />
      {mostrarInstalar && <InstalarApp onCerrar={() => setMostrarInstalar(false)} />}

      {/* Aviso de versión nueva, o actualización pedida a mano desde el menú.
          En el celular no hay forma de forzar una recarga completa, así que sin
          esto alguien puede quedarse con una versión vieja indefinidamente. */}
      {(versionNueva || mostrarActualizacion) && (
        <ActualizacionApp
          versionNueva={versionNueva}
          onCerrar={() => { descartarVersion(); setMostrarActualizacion(false); }}
        />
      )}

      {/* Botón Back to Top */}
      {showBackToTop && (
        <BackToTop onClick={scrollToTop} />
      )}

      {/* Debug info (solo desarrollo) */}
      {process.env.NODE_ENV === 'development' && (
        <div id="app-debug-info" className="fixed bottom-4 right-4 bg-black bg-opacity-75 text-white text-xs p-2 rounded max-w-xs z-50">
          <div>Página: {currentPage}</div>
          <div>Búsqueda: {searchQuery || 'N/A'}</div>
          <div>URL: {window.location.hash}</div>
        </div>
      )}
    </div>
  );
};

export default App;